import { Circle, CircleCheck, CircleX, X } from "lucide-react";
import { AppText } from "@/components/Text/AppText";
import { IScanResultItem, ScanKeysLabels } from "@/types/atsScan";
import { getScoreTailwindClassWithType } from "@/utils/scoreColors";

interface ScanItemProps {
    itemKey: string;
    item: IScanResultItem;
}

const getScoreColor = (
    score: number,
    maxScore: number,
    type: "text" | "bg",
) => {
    const percentage = (score / maxScore) * 100;
    return getScoreTailwindClassWithType(percentage, type);
};

const getFeedbackColor = (type: string) => {
    switch (type) {
        case "positive":
            return "text-success-600";
        case "negative":
            return "text-error-600";
        default:
            return "text-neutral-600";
    }
};

export const ScanItem = ({ itemKey, item }: ScanItemProps) => {
    return (
        <div className="overflow-hidden rounded-lg border border-neutral-200">
            <ProgressIndicatorBar
                itemScore={item.score}
                itemMaxScore={item.max_score}
            />
            <div className=" bg-white p-2">
                {/* Header with title and score */}
                <div className="mb-3 flex items-center justify-between">
                    <AppText
                        variant="labelsbuttons"
                        className="font-semibold capitalize text-neutral-900"
                    >
                        {ScanKeysLabels[itemKey as keyof typeof ScanKeysLabels]}
                    </AppText>
                </div>

                {/* Feedback section */}
                {item.feedback.length > 0 && (
                    <div className="space-y-2">
                        {item.feedback.map((feedback, index) => (
                            <div
                                key={index}
                                className={`flex items-start space-x-2 rounded-md bg-neutral-50 ${getFeedbackColor(feedback.type)}`}
                            >
                                <span className="mt-0.5 flex-shrink-0 text-xs">
                                    {feedback.type === "positive" ? (
                                        <CircleCheck className="h-5 w-5" />
                                    ) : feedback.type === "negative" ? (
                                        <CircleX className="h-5 w-5" />
                                    ) : (
                                        <Circle className="h-5 w-5" />
                                    )}
                                </span>
                                <AppText variant="regular">
                                    {feedback.message}
                                </AppText>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const ProgressIndicatorBar = ({
    itemScore,
    itemMaxScore,
}: {
    itemScore: number;
    itemMaxScore: number;
}) => {
    const percentage = (itemScore / itemMaxScore) * 100;
    const scoreColor = getScoreColor(itemScore, itemMaxScore, "bg");
    return (
        <div className="h-2 w-full bg-neutral-200">
            <div
                className={`h-full ${scoreColor}`}
                style={{ width: `${percentage}%` }}
            ></div>
        </div>
    );
};
